import React, { FunctionComponent, useEffect, useState, useRef, useContext, useImperativeHandle } from 'react';
import throttle from 'lodash/throttle';
import bounds from "binary-search-bounds"
import cx from 'classnames';

import styles from './fixedNav.module.less';

interface ContextType {
  postionData: {
    [key: string]: number
  };
  refeshFlag: boolean;
  current: string;
  updatePositon: (key: string, position: number) => void;
  scrollByKey: (key: string) => void;
  
}

export const WrapperContext = React.createContext<ContextType>({
  postionData: {},
  refeshFlag: false,
  current: '',
  updatePositon: () => {},
  scrollByKey: () => {},
})

export const WrapperProvider = ({ children }) => {
  const [postionData, setPostion] = useState<ContextType['postionData']>({});
  const [refeshFlag, setFlag] = useState(false);
  const [current, setCurrent] = useState('');

  const updatePositon = (key: string, position: number) => {
    if (refeshFlag) {
      setFlag(false);
      return;
    }

    setPostion(prev => {
      const copy = {...prev};
      copy[key] = position > 80 ? position - 80 : position;

      const keyArr = Object.keys(copy);
      const valueArr = Object.values(copy);
      const currentKey = keyArr[bounds.le(valueArr, window.scrollY)];
      setCurrent(() => currentKey);
      
      return copy;
    })
  }
  
  const handleScroll = throttle(() => {
    setFlag(true);
  }, 500)

  const scrollByKey = (key: string) => {
    const top = postionData[key];

    if (top) {
      window.scroll(0, top);
      return
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [postionData])

  return (
    <WrapperContext.Provider value={{ postionData, updatePositon, refeshFlag, current, scrollByKey }}>
      { children }
    </WrapperContext.Provider>
  )
}

export const NavWrapper = ({ name, children }) => {
  const currRef = useRef<HTMLDivElement>(null);
  const context = useContext(WrapperContext);

  const update = () => {
    if (currRef.current) {
      const positon = currRef.current.getBoundingClientRect().top + window.scrollY;

      context.updatePositon(name, positon);
    }
  }

  useEffect(() => {
    update();
  }, [])

  useEffect(() => {
    update();
  }, [context.refeshFlag])

  return (
    <div ref={currRef}>
      {children}
    </div>
  )
}

interface IPropsType {
  nav: string[];
  fixedNavRef: React.RefObject<HTMLDivElement>;
}

const FixedNav: FunctionComponent<IPropsType> = ({ nav, fixedNavRef }) => {
  const [isFixed, setFixed] = useState(false);

  const context = useContext(WrapperContext);

  const navChange = (key: string) => {
    const top = context.postionData[key];

    if (top) {
      window.scroll(0, top);
      return
    }

    if (key === '') {
      window.scroll(0, 0);
    }
  }

  const handleScroll = () => {
    if (window.scrollY > 400) {
      setFixed(true);
      return
    }
    setFixed(false)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll as any)
    return () => window.removeEventListener("scroll", handleScroll as any)
  }, [])

  useImperativeHandle(fixedNavRef, () => ({
    scrollByKey: (key: string) => navChange(key)
  } as any))

  return (
    <div className={cx(styles['article-fixed-nav'], { [styles['fixed']]: isFixed })}>
      <div className={styles['article-nav-wrapper']}>
        {
          nav.map((item, inx) => (
            <a
              href={`#${item}`}
              key={inx} 
              className={cx(styles['nav-item'], { [styles['nav-active']]: (item === context.current || !context.current && inx === 0) })} 
              onClick={() => navChange && navChange(item)}
            >
              {item}
              <i></i>
            </a>
          ))
        }
      </div>
    </div>
  )
}

export default FixedNav;